import { useOrganizationQuota } from "@/pages/UsageAndCost/api";
import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { calculateQuotaUsagePercent } from "@/pages/UsageAndCost/utils";

type Props = {
  organizationId: string | null;
};

const PlaygroundUsageToast = ({ organizationId }: Props) => {
  const { t, i18n } = useTranslation();
  const quota = useOrganizationQuota(organizationId);
  const used = quota.data?.used ?? 0;
  const total = quota.data?.total ?? 0;
  const usagePercent = calculateQuotaUsagePercent(used, total);

  const resetDate = React.useMemo(() => {
    if (!quota.data?.period_end) return "—";
    const date = new Date(quota.data.period_end);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  }, [i18n.language, quota.data?.period_end]);
  const getUsageMessage = (key: string) =>
    t(key, {
      defaultValue: `${used} of ${total} GPU-hrs used this period. Resets ${resetDate}`,
      used,
      total,
      resetDate,
    });
  const lastToastLevelRef = useRef<"none" | "warning" | "full">("none");

  useEffect(() => {
    if (total <= 0) {
      lastToastLevelRef.current = "none";
      return;
    } else if (usagePercent >= 80 && usagePercent < 100) {
      if (lastToastLevelRef.current !== "warning") {
        toast.warning(getUsageMessage("playground.usageReached80"), {
          toastId: "playground-usage-warning",
        });
        lastToastLevelRef.current = "warning";
      }
      return;
    } else if (usagePercent >= 100) {
      if (lastToastLevelRef.current !== "full") {
        toast.error(getUsageMessage("playground.usageReached100"), {
          toastId: "playground-usage-full",
        });
        lastToastLevelRef.current = "full";
      }
      return;
    }

    lastToastLevelRef.current = "none";
  }, [usagePercent, total, organizationId]);

  return null;
};

export default PlaygroundUsageToast;
